package songwritersnotebook.database

import app.cash.sqldelight.Query
import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Long
import kotlin.String
import kotlin.collections.Collection

public class AppDatabaseQueries(
  driver: SqlDriver,
) : SuspendingTransacterImpl(driver) {
  public fun getRhymeWordCount(): Query<Long> = Query(980_140_077, arrayOf("RhymeWordEntity"),
      driver, "AppDatabase.sq", "getRhymeWordCount", "SELECT COUNT(*) FROM RhymeWordEntity") {
      cursor ->
    cursor.getLong(0)!!
  }

  public fun <T : Any> getWordDefinition(word: String, mapper: (
    word: String,
    phonemes: String,
    stressedSuffix: String,
  ) -> T): Query<T> = GetWordDefinitionQuery(word) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!
    )
  }

  public fun getWordDefinition(word: String): Query<RhymeWordEntity> = getWordDefinition(word) {
      word_, phonemes, stressedSuffix ->
    RhymeWordEntity(
      word_,
      phonemes,
      stressedSuffix
    )
  }

  public fun <T : Any> getWordsWithSuffix(stressedSuffix: String, mapper: (
    word: String,
    phonemes: String,
    stressedSuffix: String,
  ) -> T): Query<T> = GetWordsWithSuffixQuery(stressedSuffix) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!
    )
  }

  public fun getWordsWithSuffix(stressedSuffix: String): Query<RhymeWordEntity> =
      getWordsWithSuffix(stressedSuffix) { word, phonemes, stressedSuffix_ ->
    RhymeWordEntity(
      word,
      phonemes,
      stressedSuffix_
    )
  }

  public fun <T : Any> getDefinitionsForWords(word: Collection<String>, mapper: (
    word: String,
    phonemes: String,
    stressedSuffix: String,
  ) -> T): Query<T> = GetDefinitionsForWordsQuery(word) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!
    )
  }

  public fun getDefinitionsForWords(word: Collection<String>): Query<RhymeWordEntity> =
      getDefinitionsForWords(word) { word_, phonemes, stressedSuffix ->
    RhymeWordEntity(
      word_,
      phonemes,
      stressedSuffix
    )
  }

  public fun <T : Any> getWaveform(
    url: String,
    ownerId: String,
    mapper: (
      url: String,
      ownerId: String,
      points: String,
      durationMillis: Long,
    ) -> T,
  ): Query<T> = GetWaveformQuery(url, ownerId) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getLong(3)!!
    )
  }

  public fun getWaveform(url: String, ownerId: String): Query<WaveformEntity> = getWaveform(url,
      ownerId) { url_, ownerId_, points, durationMillis ->
    WaveformEntity(
      url_,
      ownerId_,
      points,
      durationMillis
    )
  }

  public suspend fun insertRhymeWord(
    word: String,
    phonemes: String,
    stressedSuffix: String,
  ) {
    driver.execute(87_792_313, """
        |INSERT OR REPLACE INTO RhymeWordEntity(word, phonemes, stressedSuffix)
        |VALUES (?, ?, ?)
        """.trimMargin(), 3) {
          bindString(0, word)
          bindString(1, phonemes)
          bindString(2, stressedSuffix)
        }.await()
    notifyQueries(87_792_313) { emit ->
      emit("RhymeWordEntity")
    }
  }

  public suspend fun clearRhymeWords() {
    driver.execute(-1_521_273_432, """DELETE FROM RhymeWordEntity""", 0).await()
    notifyQueries(-1_521_273_432) { emit ->
      emit("RhymeWordEntity")
    }
  }

  public suspend fun insertWaveform(
    url: String,
    ownerId: String,
    points: String,
    durationMillis: Long,
  ) {
    driver.execute(-1_745_284_727, """
        |INSERT OR REPLACE INTO WaveformEntity(url, ownerId, points, durationMillis)
        |VALUES (?, ?, ?, ?)
        """.trimMargin(), 4) {
          bindString(0, url)
          bindString(1, ownerId)
          bindString(2, points)
          bindLong(3, durationMillis)
        }.await()
    notifyQueries(-1_745_284_727) { emit ->
      emit("WaveformEntity")
    }
  }

  public suspend fun deleteWaveform(url: String, ownerId: String) {
    driver.execute(-826_424_709, """DELETE FROM WaveformEntity WHERE url = ? AND ownerId = ?""", 2)
        {
          bindString(0, url)
          bindString(1, ownerId)
        }.await()
    notifyQueries(-826_424_709) { emit ->
      emit("WaveformEntity")
    }
  }

  public suspend fun clearWaveforms(ownerId: String) {
    driver.execute(762_598_780, """DELETE FROM WaveformEntity WHERE ownerId = ?""", 1) {
          bindString(0, ownerId)
        }.await()
    notifyQueries(762_598_780) { emit ->
      emit("WaveformEntity")
    }
  }

  private inner class GetWordDefinitionQuery<out T : Any>(
    public val word: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("RhymeWordEntity", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("RhymeWordEntity", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-438_440_032, """SELECT * FROM RhymeWordEntity WHERE word = ?""",
        mapper, 1) {
      bindString(0, word)
    }

    override fun toString(): String = "AppDatabase.sq:getWordDefinition"
  }

  private inner class GetWordsWithSuffixQuery<out T : Any>(
    public val stressedSuffix: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("RhymeWordEntity", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("RhymeWordEntity", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_603_307_523,
        """SELECT * FROM RhymeWordEntity WHERE stressedSuffix LIKE ?""", mapper, 1) {
      bindString(0, stressedSuffix)
    }

    override fun toString(): String = "AppDatabase.sq:getWordsWithSuffix"
  }

  private inner class GetDefinitionsForWordsQuery<out T : Any>(
    public val word: Collection<String>,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("RhymeWordEntity", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("RhymeWordEntity", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> {
      val wordIndexes = createArguments(count = word.size)
      return driver.executeQuery(null,
          """SELECT * FROM RhymeWordEntity WHERE word IN $wordIndexes""", mapper, word.size) {
            word.forEachIndexed { index, word_ ->
              bindString(index, word_)
            }
          }
    }

    override fun toString(): String = "AppDatabase.sq:getDefinitionsForWords"
  }

  private inner class GetWaveformQuery<out T : Any>(
    public val url: String,
    public val ownerId: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("WaveformEntity", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("WaveformEntity", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_750_502_688,
        """SELECT * FROM WaveformEntity WHERE url = ? AND (ownerId = ? OR ownerId = '')""", mapper,
        2) {
      bindString(0, url)
      bindString(1, ownerId)
    }

    override fun toString(): String = "AppDatabase.sq:getWaveform"
  }
}
